// pages/OpenLeaks/components/LeakStats/LeakStats.tsx (updated)
import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { LeakStats, statusLabels, statusColors } from "features/openleak";
import './LeakStats.scss';
import { ReactComponent as ChartGraph } from "assets/img/icons/icon_stats.svg";
import { ReactComponent as Users } from "assets/img/icons/icon_Users.svg";
import { ReactComponent as Company } from "assets/img/icons/icon_company.svg";

// Enregistrement des composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface LeakStatsProps {
  stats: LeakStats;
}

const LeakStatsComponent: React.FC<LeakStatsProps> = ({ stats }) => {
  const safeStats = stats || {
    totalLeaks: 0,
    totalAffected: 0,
    byStatus: {},
    byMonth: {},
    topCompanies: []
  };

  // Configuration du graphique par statut (Doughnut)
  const statusChartData = useMemo(() => {
    const byStatus = safeStats.byStatus || {};
    const labels = Object.keys(byStatus).map(key => statusLabels[key] || key);
    const data = Object.values(byStatus);
    const backgroundColors = Object.keys(byStatus).map(key => statusColors[key] || '#9CA3AF');

    if (data.length === 0) {
      return {
        labels: ['Aucune donnée'],
        datasets: [{ data: [1], backgroundColor: ['#E2E8F0'], borderWidth: 0 }]
      };
    }

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderWidth: 0,
          borderRadius: 4,
          spacing: 2,
        },
      ],
    };
  }, [safeStats.byStatus]);

  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 15,
          font: { size: 12, weight: '500' },
          usePointStyle: true,
          pointStyle: 'circle',
          color: '#475569',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = Object.values(safeStats.byStatus || {}).reduce((a, b) => a + b, 0);
            if (total === 0) return `${label}: ${value}`;
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    cutout: '60%',
  };

  // Configuration du graphique Top Entreprises (Bar)
  const topCompaniesData = useMemo(() => {
    const topCompanies = safeStats.topCompanies || [];
    const top6 = topCompanies.slice(0, 6);

    if (top6.length === 0) {
      return {
        labels: ['Aucune donnée'],
        datasets: [{ label: 'Nombre de fuites', data: [0], backgroundColor: '#E2E8F0' }]
      };
    }

    return {
      labels: top6.map(item => item.company?.length > 15 ? item.company.substring(0, 15) + '...' : (item.company || 'Inconnu')),
      datasets: [
        {
          label: 'Nombre de fuites',
          data: top6.map(item => item.count || 0),
          backgroundColor: '#3b82f6',
          borderRadius: 8,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
          hoverBackgroundColor: '#2563eb',
        },
      ],
    };
  }, [safeStats.topCompanies]);

  const topCompaniesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            return `📊 ${context.parsed.y || 0} fuite(s)`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          stepSize: 1,
          color: '#64748b',
        },
        title: {
          display: true,
          text: 'Nombre de fuites',
          color: '#94a3b8',
          font: { size: 11, weight: '500' },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748b',
          font: { size: 11 },
        },
      },
    },
  };

  // Configuration du graphique d'évolution temporelle (Line)
  const evolutionData = useMemo(() => {
    const byMonth = safeStats.byMonth || {};
    const months = Object.keys(byMonth).sort();
    const counts = months.map(month => byMonth[month] || 0);

    if (months.length === 0) {
      return {
        labels: ['Aucune donnée'],
        datasets: [{
          label: 'Nombre de fuites',
          data: [0],
          borderColor: '#E2E8F0',
          backgroundColor: 'rgba(226, 232, 240, 0.1)',
          fill: true,
        }]
      };
    }

    const formattedMonths = months.map(month => {
      const [year, monthNum] = month.split('-');
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
    });

    return {
      labels: formattedMonths,
      datasets: [
        {
          label: 'Fuites détectées',
          data: counts,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.05)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: 'white',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#2563eb',
        },
      ],
    };
  }, [safeStats.byMonth]);

  const evolutionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            return `📈 ${context.parsed.y || 0} fuite(s)`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          stepSize: 1,
          color: '#64748b',
        },
        title: {
          display: true,
          text: 'Nombre de fuites',
          color: '#94a3b8',
          font: { size: 11, weight: '500' },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748b',
          font: { size: 10 },
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Calcul des métriques globales
  const totalAffectedFormatted = useMemo(() => {
    const totalAffected = safeStats.totalAffected || 0;
    if (totalAffected === 0) return 'Non spécifié';
    if (totalAffected >= 1_000_000_000) {
      return `${(totalAffected / 1_000_000_000).toFixed(1)}Md`;
    }
    if (totalAffected >= 1_000_000) {
      return `${(totalAffected / 1_000_000).toFixed(1)}M`;
    }
    if (totalAffected >= 1_000) {
      return `${(totalAffected / 1_000).toFixed(0)}k`;
    }
    return totalAffected.toLocaleString();
  }, [safeStats.totalAffected]);

  const totalLeaks = safeStats.totalLeaks || 0;
  const monthsCount = Object.keys(safeStats.byMonth || {}).length;
  const companiesCount = (safeStats.topCompanies || []).length;

  return (
    <div className="leak-stats">
      {/* Metrics Cards - Beautiful Glassmorphism */}
      <div className="leak-stats__metrics">
        <div className="metric-card metric-card--blue">
          <div className="metric-card__icon">
            <ChartGraph
              fill={"#2f62ae"}
              height={30}
              className="PageHeader__icon"
              width={30}
            />
          </div>
          <div className="metric-card__content">
            <div className="metric-card__value">{totalLeaks}</div>
            <div className="metric-card__label">Fuites documentées</div>
          </div>
          <div className="metric-card__trend">
            <span className="trend-up">+12%</span>
          </div>
        </div>

        <div className="metric-card metric-card--purple">
          <div className="metric-card__icon">
            <Users
              fill={"#2f62ae"}
              height={30}
              className="PageHeader__icon"
              width={30}
            />
          </div>
          <div className="metric-card__content">
            <div className="metric-card__value">{totalAffectedFormatted}</div>
            <div className="metric-card__label">Personnes impactées</div>
          </div>
        </div>

        {/*<div className="metric-card metric-card--green">
          <div className="metric-card__icon">📅</div>
          <div className="metric-card__content">
            <div className="metric-card__value">{monthsCount}</div>
            <div className="metric-card__label">Mois concernés</div>
          </div>
        </div>*/}

        <div className="metric-card metric-card--orange">
          <div className="metric-card__icon">
            <Company
              fill={"#2f62ae"}
              height={30}
              className="PageHeader__icon"
              width={30}
            />
          </div>
          <div className="metric-card__content">
            <div className="metric-card__value">{companiesCount}</div>
            <div className="metric-card__label">Entreprises touchées</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      {/*<div className="leak-stats__charts">
         Status Distribution - Doughnut
        <div className="chart-card">
          <div className="chart-card__header">
            <div className="chart-card__title-wrapper">
              <div className="chart-card__icon">🎯</div>
              <div>
                <h3 className="chart-card__title">Distribution des statuts</h3>
                <p className="chart-card__subtitle">Fiabilité des informations</p>
              </div>
            </div>
          </div>
          <div className="chart-card__content chart-card__content--doughnut">
            <Doughnut data={statusChartData} options={statusChartOptions as any} />
          </div>
        </div>

         Top Companies - Bar
        <div className="chart-card">
          <div className="chart-card__header">
            <div className="chart-card__title-wrapper">
              <div className="chart-card__icon"></div>
              <div>
                <h3 className="chart-card__title">Entreprises leaders</h3>
                <p className="chart-card__subtitle">Top 6 des plus touchées</p>
              </div>
            </div>
          </div>
          <div className="chart-card__content chart-card__content--bar">
            <Bar data={topCompaniesData} options={topCompaniesOptions as any} />
          </div>
        </div>

         Evolution Timeline - Line
        <div className="chart-card">
          <div className="chart-card__header">
            <div className="chart-card__title-wrapper">
              <div className="chart-card__icon"></div>
              <div>
                <h3 className="chart-card__title">Évolution temporelle</h3>
                <p className="chart-card__subtitle">Tendance des fuites par mois</p>
              </div>
            </div>
          </div>
          <div className="chart-card__content chart-card__content--line">
            <Line data={evolutionData} options={evolutionOptions as any} />
          </div>
        </div>
      </div>*/}
    </div>
  );
};

export default LeakStatsComponent;